/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Cookie Store API wrapper for CDU compliance.
 *
 * This wrapper intercepts the modern Cookie Store API (window.cookieStore)
 * and uses in-memory storage when consent is not accepted.
 *
 * Note: Cookie Store API is not available in all browsers (primarily Chromium-based).
 * This wrapper provides a fallback for browsers that don't support it.
 */

import { getInMemoryCookieStore, hasConsent } from './cookie-store-memory';

/**
 * Original cookieStore reference (if available).
 */
let originalCookieStore: any = null;

/**
 * Migrates a single cookie into the real Cookie Store, applying default
 * attributes (1-year expiry, root path, `lax` same-site) when unset.
 *
 * @param cookie The in-memory cookie record to migrate.
 */
async function migrateCookie(cookie: any): Promise<void> {
  const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
  try {
    await originalCookieStore.set({
      name: cookie.name,
      value: cookie.value,
      expires: cookie.expires || Date.now() + ONE_YEAR_MS,
      path: cookie.path || '/',
      sameSite: cookie.sameSite || 'lax',
    });
    console.log(`[CookieStoreWrapper] Migrated cookie: ${cookie.name}`);
  } catch (error) {
    console.error(`[CookieStoreWrapper] Failed to migrate cookie ${cookie.name}:`, error);
  }
}

/**
 * Migrates cookies from in-memory store to real Cookie Store API.
 */
export async function migrateInMemoryToRealCookieStore(): Promise<void> {
  if (!originalCookieStore) {
    console.warn('[CookieStoreWrapper] Real cookieStore not available');
    return;
  }

  console.log('[CookieStoreWrapper] Migrating in-memory cookies to real cookieStore');

  const memory = getInMemoryCookieStore();
  const cookies = await memory.getAll();

  await Promise.all(cookies.map(migrateCookie));

  memory.clear();
  console.log('[CookieStoreWrapper] Cookie migration complete');
}

/**
 * Clears in-memory cookie store.
 */
export function clearInMemoryCookieStore(): void {
  getInMemoryCookieStore().clear();
  console.log('[CookieStoreWrapper] In-memory cookie store cleared');
}

/**
 * Gets all cookies from in-memory cookie store (for debugging).
 */
export function getInMemoryCookieStoreContents(): Record<string, string> {
  return getInMemoryCookieStore().getContents();
}

/**
 * Normalizes the polymorphic Cookie Store `set` arguments into a uniform shape,
 * accepting either `(name, value, options)` or a single options object.
 *
 * @param nameOrOptions Cookie name, or an options object with `name`/`value`.
 * @param value Cookie value when the first argument is a name string.
 * @param options Optional attributes when using the positional form.
 * @returns The resolved `{ finalName, finalValue, finalOptions }` tuple.
 */
function resolveSetArguments(nameOrOptions: any, value?: string, options?: any) {
  if (typeof nameOrOptions === 'string') {
    return { finalName: nameOrOptions, finalValue: value!, finalOptions: options };
  }
  return { finalName: nameOrOptions.name, finalValue: nameOrOptions.value, finalOptions: nameOrOptions };
}

/**
 * Determines whether operations should target the real Cookie Store API:
 * true only when CDU consent is accepted AND the real store is available.
 *
 * @returns True to use the real Cookie Store, false to use in-memory storage.
 */
function useRealCookieStore(): boolean {
  return hasConsent() && Boolean(originalCookieStore);
}

/**
 * Reads a single cookie, routing to the real store or in-memory backend.
 *
 * @param memory The in-memory cookie store fallback.
 * @param name The cookie name to read.
 * @returns The cookie, or null when absent.
 */
function wrappedGet(memory: ReturnType<typeof getInMemoryCookieStore>, name: string): Promise<any | null> {
  return useRealCookieStore() ? originalCookieStore.get(name) : memory.get(name);
}

/**
 * Reads all cookies, routing to the real store or in-memory backend.
 *
 * @param memory The in-memory cookie store fallback.
 * @param options Optional name/url filter.
 * @returns The list of matching cookies.
 */
function wrappedGetAll(
  memory: ReturnType<typeof getInMemoryCookieStore>,
  options?: { name?: string; url?: string },
): Promise<any[]> {
  return useRealCookieStore() ? originalCookieStore.getAll(options) : memory.getAll(options);
}

/**
 * Writes a cookie, routing to the real store (preserving the original call
 * shape) or the in-memory backend.
 *
 * @param memory The in-memory cookie store fallback.
 * @param nameOrOptions Cookie name or options object.
 * @param value Cookie value for the positional form.
 * @param options Optional attributes for the positional form.
 */
function wrappedSet(
  memory: ReturnType<typeof getInMemoryCookieStore>,
  nameOrOptions: any,
  value?: string,
  options?: any,
): Promise<void> {
  const { finalName, finalValue, finalOptions } = resolveSetArguments(nameOrOptions, value, options);
  if (!useRealCookieStore()) {
    return memory.set(finalName, finalValue, finalOptions);
  }
  return typeof nameOrOptions === 'string'
    ? originalCookieStore.set(finalName, finalValue, finalOptions)
    : originalCookieStore.set(nameOrOptions);
}

/**
 * Deletes a cookie, routing to the real store or in-memory backend.
 *
 * @param memory The in-memory cookie store fallback.
 * @param name The cookie name to delete.
 */
function wrappedDelete(memory: ReturnType<typeof getInMemoryCookieStore>, name: string): Promise<void> {
  return useRealCookieStore() ? originalCookieStore.delete(name) : memory.delete(name);
}

/**
 * Forwards an event-listener call to the real Cookie Store when it supports
 * the given method; otherwise a no-op (in-memory store has no events).
 *
 * @param method The listener method to forward ('add' or 'remove').
 * @param type The event type.
 * @param listener The event listener.
 */
function forwardListener(method: 'addEventListener' | 'removeEventListener', type: string, listener: EventListener): void {
  if (originalCookieStore?.[method]) {
    originalCookieStore[method](type, listener);
  }
}

/**
 * Creates a Cookie Store API wrapper that enforces CDU consent.
 */
function createCookieStoreWrapper(): any {
  const memory = getInMemoryCookieStore();

  return {
    get: (name: string): Promise<any | null> => wrappedGet(memory, name),
    getAll: (options?: { name?: string; url?: string }): Promise<any[]> => wrappedGetAll(memory, options),
    set: (nameOrOptions: any, value?: string, options?: any): Promise<void> =>
      wrappedSet(memory, nameOrOptions, value, options),
    delete: (name: string): Promise<void> => wrappedDelete(memory, name),
    addEventListener: (type: string, listener: EventListener): void =>
      forwardListener('addEventListener', type, listener),
    removeEventListener: (type: string, listener: EventListener): void =>
      forwardListener('removeEventListener', type, listener),
  };
}

/**
 * Initializes the Cookie Store API wrapper.
 * Note: This only works in browsers that support the Cookie Store API.
 */
export function initializeCookieStoreWrapper(): void {
  if (typeof window === 'undefined' || !(window as any).cookieStore) {
    console.log('[CookieStoreWrapper] Cookie Store API not available in this browser');
    return;
  }

  if ((window as any).__originalCookieStore) {
    return;
  }

  const browserWindow = window as any;
  originalCookieStore = browserWindow.cookieStore;
  (window as any).__originalCookieStore = originalCookieStore;

  try {
    Object.defineProperty(window, 'cookieStore', {
      value: createCookieStoreWrapper(),
      writable: true,
      configurable: true,
      enumerable: true,
    });
    console.log('[CookieStoreWrapper] Initialized - using in-memory storage until CDU consent is accepted');
  } catch (error) {
    console.warn('[CookieStoreWrapper] Failed to override cookieStore (may be non-configurable):', error);
  }
}

/**
 * Gets the original cookie store (for testing purposes).
 *
 * @returns {any} The original Cookie Store API instance, if available.
 */
export function getOriginalCookieStore(): any {
  return originalCookieStore;
}
